import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, IconButtonProps, TextField, TextFieldProps } from '@mui/material';
import clsx from 'clsx';
import React, { forwardRef, memo, useCallback, useRef } from 'react';
import { useMergeRef } from 'hooks/use-merge-ref';
import { objectSlotProps } from 'utils/slot-props';

/**
 * The app's TextField: standard variant, full width, and a clear button in
 * the input's end adornment. MUI 9 shape (`slotProps.input` / `.inputLabel` /
 * `.htmlInput` / `.formHelperText`; the `InputProps` family is gone from
 * TextField). `slotProps.input` object form only -- the clear button is
 * composed into it.
 */
interface Props {
  disableClearable?: boolean;
  onClear?: (e: React.MouseEvent) => void;
  ClearIconProps?: Partial<IconButtonProps>;
}

export type AppInputProps = Props & TextFieldProps;
const Input = (
  {
    disableClearable = false,
    onClear,
    ClearIconProps,
    inputRef,
    slotProps,
    ...props
  }: AppInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const innerRef = useRef<HTMLInputElement | null>(null);
  const inputSlotProps = objectSlotProps(slotProps?.input);
  const mergedInputRef = useMergeRef(ref, innerRef, inputRef || null);

  const _onClear = useCallback(
    (e: React.MouseEvent) => {
      const input = innerRef.current;
      if (input) {
        let lastValue = input.value;
        input.value = '';
        let event = new Event('input', { bubbles: true });
        // @ts-ignore
        let tracker = input._valueTracker;
        if (tracker) tracker.setValue(lastValue);
        input.dispatchEvent(event);
      }
      if (onClear) {
        onClear(e);
      }
    },
    [onClear],
  );

  return (
    <TextField
      inputRef={mergedInputRef}
      variant={'standard'}
      fullWidth
      {...props}
      slotProps={{
        ...slotProps,
        input: {
          ...inputSlotProps,
          endAdornment: (
            <>
              {!disableClearable && (
                <IconButton
                  {...ClearIconProps}
                  // N2 (T5): a hook for the patient card's general tab, which
                  // hides the clear X below md (no style of its own)
                  className={clsx('app-input__clear', ClearIconProps?.className)}
                  disabled={props.disabled}
                  style={{ visibility: !props.value ? 'hidden' : 'visible' }}
                  onClick={_onClear}
                  size={'small'}
                >
                  <ClearIcon fontSize={'small'} />
                </IconButton>
              )}
              {inputSlotProps?.endAdornment}
            </>
          ),
        },
      }}
    />
  );
};

// React 19 types: a JSX component takes one argument; the memo(forwardRef()) type is the component's type
export const AppInput = memo(forwardRef(Input));
